import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme, cssVar } from 'styles/theme';
import { isAltFunctionKey, isElementVisible } from 'utils/domVisibility';

const STORAGE_PREFIX = 'searchPanelCollapsed';

function storageKeyOf(prefsUserId, storageKey) {
  return [STORAGE_PREFIX, prefsUserId || 'anon', storageKey || 'default'].join('|');
}

function readCollapsed(prefsUserId, storageKey, fallback) {
  try {
    const raw = localStorage.getItem(storageKeyOf(prefsUserId, storageKey));
    if (raw === 'Y') return true;
    if (raw === 'N') return false;
  } catch {
    /* ignore */
  }
  return !!fallback;
}

function writeCollapsed(prefsUserId, storageKey, collapsed) {
  try {
    localStorage.setItem(storageKeyOf(prefsUserId, storageKey), collapsed ? 'Y' : 'N');
  } catch {
    /* ignore */
  }
}

const Shell = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: var(--erp-radius-panel, ${theme.borderRadius.md});
  background: ${cssVar('color-bg-white', theme.colors.white)};
  margin-bottom: var(--erp-content-gap, 6px);
  box-shadow: var(--erp-panel-shadow, none);
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: var(--erp-search-bar-min-height, 28px);
  padding: var(--erp-search-bar-padding, 2px 6px 2px 10px);
  background: ${cssVar('color-bg-light', theme.colors.grid.readonlyBg)};
  border-bottom: ${(p) => (p.$collapsed ? 'none' : `1px solid ${cssVar('color-border', theme.colors.border)}`)};
  border-radius: ${(p) =>
    p.$collapsed
      ? 'var(--erp-radius-panel, 8px)'
      : 'var(--erp-radius-panel, 8px) var(--erp-radius-panel, 8px) 0 0'};
`;

const BarLabel = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: 600;
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Summary = styled.span`
  font-weight: 500;
  color: ${cssVar('color-text-tertiary', theme.colors.text.tertiary)};
  margin-left: 6px;
`;

const ToggleBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 28px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  background: ${cssVar('color-bg-white', theme.colors.white)};
  color: ${cssVar('color-denary', theme.colors.denary)};
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${cssVar('color-bg-medium', theme.colors.background.medium)};
    border-color: ${cssVar('color-septenary', theme.colors.septenary)};
  }

  &:focus-visible {
    outline: 2px solid ${cssVar('color-primary', theme.colors.primary)};
    outline-offset: 1px;
  }
`;

const Body = styled.div`
  display: ${(p) => (p.$collapsed ? 'none' : 'block')};
  min-width: 0;
`;

/** 셀렉트박스 네이티브 화살표와 동일한 둥근 획 chevron */
const ChevronIcon = ({ up = false }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ flexShrink: 0 }}
  >
    <polyline points={up ? '6 15 12 9 18 15' : '6 9 12 15 18 9'} />
  </svg>
);

/**
 * 조회조건(FormArea) 리본형 축소 패널
 *
 * - Alt+F1 토글
 * - 접어도 children 언마운트하지 않음 → getValues / F7 조회 유지
 * - localStorage: searchPanelCollapsed|{userId}|{storageKey}
 *
 * @param {string} storageKey - 화면별 고유 키 (필수)
 * @param {string} [prefsUserId]
 * @param {string|React.ReactNode} [summary] - 접힘 시 표시 요약
 * @param {boolean} [defaultCollapsed]
 * @param {React.ReactNode} children
 */
const CollapsibleSearchPanel = ({
  storageKey,
  prefsUserId = '',
  summary = '',
  defaultCollapsed = false,
  title = '검색조건',
  children,
}) => {
  const [collapsed, setCollapsed] = useState(() =>
    readCollapsed(prefsUserId, storageKey, defaultCollapsed)
  );
  const shellRef = useRef(null);

  useEffect(() => {
    setCollapsed(readCollapsed(prefsUserId, storageKey, defaultCollapsed));
  }, [prefsUserId, storageKey, defaultCollapsed]);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(prefsUserId, storageKey, next);
      return next;
    });
  }, [prefsUserId, storageKey]);

  useEffect(() => {
    const onKey = (e) => {
      if (!isAltFunctionKey(e, 'F1')) return;
      // 비활성 탭(display:none)의 패널은 가로채지 않음 → 활성 탭 리스너가 처리
      if (!isElementVisible(shellRef.current)) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
      toggle();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [toggle]);

  const barText = useMemo(() => {
    if (!collapsed) return title;
    return (
      <>
        {title}
        {summary ? <Summary>· {summary}</Summary> : null}
      </>
    );
  }, [collapsed, title, summary]);

  return (
    <Shell ref={shellRef} data-search-panel={collapsed ? 'collapsed' : 'expanded'}>
      <Bar $collapsed={collapsed}>
        <BarLabel title={typeof summary === 'string' ? String(summary) : undefined}>
          {barText}
          <Summary style={{ marginLeft: 8 }}>Alt+F1</Summary>
        </BarLabel>
        <ToggleBtn
          type="button"
          onClick={toggle}
          title={collapsed ? '검색조건 펼치기 (Alt+F1)' : '검색조건 접기 (Alt+F1)'}
          aria-expanded={!collapsed}
          aria-label={collapsed ? '검색조건 펼치기' : '검색조건 접기'}
        >
          {collapsed ? (
            <>
              펼치기 <ChevronIcon />
            </>
          ) : (
            <>
              접기 <ChevronIcon up />
            </>
          )}
        </ToggleBtn>
      </Bar>
      <Body $collapsed={collapsed} aria-hidden={collapsed}>
        {children}
      </Body>
    </Shell>
  );
};

export default CollapsibleSearchPanel;
